import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { masterApi, apiError, formatPhone } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Send, Link2, CheckCircle2, RefreshCw, AlertCircle, ChevronLeft, ChevronRight, Check, QrCode, Smartphone, Power, Loader2, Trash2, Settings, Save } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

const TIPOS_LABEL = {
  otp: "OTP", saudade: "Saudade", pesquisa: "Pesquisa",
  agradecimento: "Agradecimento", indicacao: "Indicação", campanha: "Campanha",
  geral: "Geral", teste: "Teste",
};

// Máscara progressiva de telefone BR: (DD) NNNNN-NNNN (celular) / (DD) NNNN-NNNN (fixo).
function maskTel(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export default function MasterWhatsApp() {
  const [sp] = useSearchParams();
  // permite abrir direto numa aba via ?tab= (ex.: link "Conectar" das Integrações → ?tab=status)
  const tabInicial = ["envio", "status", "historico"].includes(sp.get("tab")) ? sp.get("tab") : "envio";
  const [tab, setTab] = useState(tabInicial); // envio | status (Conexão) | historico

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-whatsapp">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">WhatsApp · Mensageria</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Mensageria</h1>
      </div>

      <div className="flex gap-1 p-1 bg-white/80 border border-neutral-200/70 rounded-full w-fit">
        {[
          ["envio", "Envio único"],
          ["status", "Conexão"],
          ["historico", "Histórico"],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`wa-tab-${k}`}
                  className={`px-4 h-8 rounded-full text-[12px] font-medium transition-colors ${
                    tab === k ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
                  }`}>{lbl}</button>
        ))}
      </div>

      {tab === "envio" && <EnvioUnico />}
      {tab === "status" && <Conexao />}
      {tab === "historico" && <Historico />}
    </div>
  );
}

function Conexao() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState(null);
  const [awaiting, setAwaiting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [limpando, setLimpando] = useState(false);
  const [tel, setTel] = useState("");
  const [sending, setSending] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cfgForm, setCfgForm] = useState({ instance_id: "", token: "", client_token: "" });
  const [savingCfg, setSavingCfg] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await masterApi.get("/master/whatsapp/status");
      setStatus(data);
      if (data.connected) { setQr(null); setAwaiting(false); }
    } catch (e) { modal.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadCfg = async () => {
    try {
      const { data } = await masterApi.get("/master/whatsapp/config");
      setCfg(data);
      setCfgForm({ instance_id: data.instance_id || "", token: "", client_token: "" });
    } catch { /* silencioso */ }
  };
  useEffect(() => { loadCfg(); }, []);

  const saveCfg = async () => {
    if (!cfgForm.instance_id.trim()) { modal.error("Informe o Instance ID da Z-API"); return; }
    setSavingCfg(true);
    try {
      await masterApi.post("/master/whatsapp/config", cfgForm);
      modal.success("Configuração Z-API salva");
      await loadCfg(); await load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSavingCfg(false); }
  };

  // Polling enquanto aguarda o pareamento (QR aberto)
  useEffect(() => {
    if (!awaiting) return;
    const tick = async () => {
      try {
        const { data } = await masterApi.get("/master/whatsapp/status");
        setStatus(data);
        if (data.connected) { setQr(null); setAwaiting(false); modal.success("WhatsApp conectado!"); return; }
        const r = await masterApi.get("/master/whatsapp/qrcode");
        if (r.data?.qr_code) setQr(r.data.qr_code);
      } catch { /* silencioso durante polling */ }
    };
    const t = setInterval(tick, 3500);
    return () => clearInterval(t);
  }, [awaiting]);

  const conectar = async () => {
    setConnecting(true); setQr(null);
    try {
      const { data } = await masterApi.post("/master/whatsapp/conectar");
      if (data.ok === false) { modal.error(data.detail || "Falha ao conectar"); }
      else { if (data.qr_code) setQr(data.qr_code); setAwaiting(true); }  // QR aparece inline no card (sem modal)
      load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setConnecting(false); }
  };

  const desconectar = async () => {
    try {
      await masterApi.post("/master/whatsapp/desconectar");
      setQr(null); setAwaiting(false); modal.success("Sessão desconectada"); load();
    } catch (e) { modal.error(apiError(e)); }
  };

  const limpar = async () => {
    const ok = await modal.confirm({
      title: "Limpar conexão",
      message: "A conexão do WhatsApp será reiniciada — isso limpa estados antigos que podem travar o QR Code.",
      details: ["Depois você gera um novo QR Code para reconectar o WhatsApp."],
      confirmText: "Limpar tudo",
      cancelText: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    setLimpando(true);
    try {
      const { data } = await masterApi.post("/master/whatsapp/limpar");
      const n = data.removidas?.length || 0;
      if (data.ok) modal.success(`Conexões limpas — ${n} sessão(ões) removida(s). Gere um novo QR Code.`);
      else modal.error(data.detail || "Falha ao limpar conexões");
      setQr(null); setAwaiting(false); load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setLimpando(false); }
  };

  const testar = async () => {
    if (!tel.trim()) { modal.error("Informe um telefone (com DDD)"); return; }
    setSending(true);
    try {
      const { data } = await masterApi.post("/master/whatsapp/teste", { telefone: tel });
      if (data.ok) modal.success("Mensagem de teste enviada!");
      else modal.error("Falha no envio. Verifique se o WhatsApp está conectado.");
    } catch (e) { modal.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (!status) return <div className="card-soft rounded-xl p-5 text-sm text-neutral-500">Carregando status...</div>;

  const ok = status.connected;
  const naoConfig = status.status === "nao_configurado";
  const PROV = { zapi: "Z-API", mock: "Mock" }[status.modo] || "WhatsApp";

  return (
    <div className="space-y-3" data-testid="wa-conexao-section">
      {/* Configuração Z-API (parametrizável pela UI) */}
      <div className="card-soft rounded-2xl p-5" data-testid="wa-config-card">
        <div className="flex items-center gap-2 mb-1">
          <Settings size={18} className="text-[color:var(--brand-blue)]" />
          <div className="font-serif-display text-lg">Configuração Z-API</div>
        </div>
        <p className="text-[12px] text-neutral-500 mb-3">
          Credenciais da sua instância no painel <b>app.z-api.io</b>. Ficam salvas no sistema — sem mexer no servidor.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Instance ID</label>
            <input data-testid="zapi-instance" value={cfgForm.instance_id}
                   onChange={(e) => setCfgForm({ ...cfgForm, instance_id: e.target.value })}
                   placeholder="3E…" className={inputCls + " mt-1"} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Token</label>
            <input data-testid="zapi-token" type="password" value={cfgForm.token}
                   onChange={(e) => setCfgForm({ ...cfgForm, token: e.target.value })}
                   placeholder={cfg?.token_set ? "•••••• (salvo — deixe em branco p/ manter)" : "token da instância"}
                   className={inputCls + " mt-1"} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Client-Token <span className="normal-case text-neutral-400">(opcional)</span></label>
            <input data-testid="zapi-client" type="password" value={cfgForm.client_token}
                   onChange={(e) => setCfgForm({ ...cfgForm, client_token: e.target.value })}
                   placeholder={cfg?.client_token_set ? "•••• (salvo)" : "Account Security Token"}
                   className={inputCls + " mt-1"} />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-[11px] text-neutral-400">
            {cfg?.token_set ? "Token configurado ✓" : "Token ainda não configurado"}
          </span>
          <button onClick={saveCfg} disabled={savingCfg} data-testid="zapi-save" className="btn-action btn-salvar">
            {savingCfg ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {savingCfg ? "Salvando..." : "Salvar configuração"}
          </button>
        </div>
      </div>

      {/* Status da conexão */}
      <div className={`card-soft rounded-2xl p-5 border-l-4 ${ok ? "border-emerald-500" : "border-rose-500"}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="chip text-[10px] py-0.5 mb-1.5"
                 style={{ background: ok ? "rgba(16,185,129,0.18)" : "rgba(244,63,94,0.18)",
                          color: ok ? "#047857" : "#9F1239" }}>
              <span className="pulse-dot" /> {ok ? "Conectado" : "Desconectado"}
            </div>
            <div className="font-serif-display text-xl">
              {ok ? "WhatsApp ativo e pronto" : "WhatsApp não está conectado"}
            </div>
            <div className="text-[12px] text-neutral-600 mt-1">
              Provedor: <b>{PROV}</b>{status.numero ? <> · Número <b>{status.numero}</b></> : null} · {status.detail}
            </div>
          </div>
          <div className="flex gap-1.5">
            <button onClick={load} disabled={loading} data-testid="wa-status-reload"
                    className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Atualizar
            </button>
            {ok && (
              <button onClick={desconectar} data-testid="wa-desconectar"
                      className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 text-rose-700">
                <Power size={13} /> Desconectar
              </button>
            )}
            {!naoConfig && (
              <button onClick={limpar} disabled={limpando} data-testid="wa-limpar"
                      title="Reinicia a conexão do WhatsApp (corrige QR que aparece e some)"
                      className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 text-amber-700">
                <Trash2 size={13} className={limpando ? "animate-pulse" : ""} /> {limpando ? "Limpando..." : "Limpar conexões"}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          <Mini label="Provedor" value={PROV} />
          <Mini label="Conectado" value={status.connected ? "Sim" : "Não"} accent={status.connected ? "emerald" : null} />
          <Mini label="Celular pareado" value={status.smartphone_connected ? "Sim" : "Não"} />
          <Mini label="Configurado" value={status.configured ? "Sim" : "Não"} accent={status.configured ? null : "rose"} />
        </div>

        <div className="grid grid-cols-3 gap-2 mt-2">
          <Mini label="Envios 24h" value={String(status.envios_24h?.total ?? 0)} />
          <Mini label="Sucesso" value={String(status.envios_24h?.sucesso ?? 0)} accent="emerald" />
          <Mini label="Falha" value={String(status.envios_24h?.falha ?? 0)} accent={status.envios_24h?.falha ? "rose" : null} />
        </div>
      </div>

      {/* Token ausente */}
      {naoConfig && (
        <div className="card-soft rounded-xl p-4 flex items-start gap-3 bg-amber-50 border border-amber-200">
          <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={16} />
          <div className="text-[12px] text-amber-900 leading-relaxed">
            <b>{PROV} não configurado.</b> Defina as credenciais do provedor no servidor para habilitar a geração do QR Code e os envios.
          </div>
        </div>
      )}

      {/* QR Code / Conectar */}
      {!ok && !naoConfig && (
        <div className="card-soft rounded-2xl p-5" data-testid="wa-qr-card">
          <div className="flex items-center gap-2 mb-3">
            <QrCode size={18} className="text-[color:var(--brand-blue)]" />
            <div className="font-serif-display text-lg">Conectar WhatsApp</div>
          </div>

          {qr ? (
            <div className="flex flex-col md:flex-row items-center gap-5">
              <div className="bg-white rounded-2xl border border-[var(--line)] p-3 shadow-sm shrink-0">
                <img src={qr} alt="QR Code do WhatsApp" data-testid="wa-qr-img"
                     className="w-[230px] h-[230px] object-contain" />
              </div>
              <div className="text-[13px] text-neutral-600 leading-relaxed space-y-2">
                <div className="flex items-center gap-2 text-[color:var(--brand-blue)] font-medium">
                  <Loader2 size={14} className="animate-spin" /> Aguardando leitura...
                </div>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Abra o <b>WhatsApp</b> no celular do posto</li>
                  <li>Toque em <b>Aparelhos conectados</b></li>
                  <li>Toque em <b>Conectar aparelho</b></li>
                  <li>Aponte a câmera para este QR Code</li>
                </ol>
                <button onClick={conectar} disabled={connecting} data-testid="wa-qr-refresh"
                        className="btn-ghost h-9 px-3 text-[12px] font-medium inline-flex items-center gap-1.5 mt-1">
                  <RefreshCw size={13} className={connecting ? "animate-spin" : ""} /> Gerar novo QR
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start gap-3">
              <p className="text-[13px] text-neutral-600">
                {awaiting
                  ? "Gerando o QR Code, aguarde alguns segundos — ele aparece aqui mesmo."
                  : "Gere o QR Code e escaneie com o WhatsApp do posto para ativar o envio de mensagens (OTP, Saudade, Pesquisa, Campanhas)."}
              </p>
              <button onClick={conectar} disabled={connecting || awaiting} data-testid="wa-conectar"
                      className="btn-action btn-salvar">
                {(connecting || awaiting) ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />}
                {connecting ? "Gerando..." : awaiting ? "Gerando QR Code..." : "Gerar QR Code"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Teste de envio */}
      <div className="card-soft rounded-2xl p-5" data-testid="wa-teste-card">
        <div className="flex items-center gap-2 mb-1">
          <Smartphone size={18} className="text-[color:var(--brand-blue)]" />
          <div className="font-serif-display text-lg">Testar envio</div>
        </div>
        <p className="text-[12px] text-neutral-500 mb-3">Envie uma mensagem de teste para confirmar que a conexão está entregando.</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Telefone (com DDD)</label>
            <input data-testid="wa-teste-tel" value={tel} onChange={(e) => setTel(maskTel(e.target.value))}
                   inputMode="numeric" maxLength={16} placeholder="(51) 99999-9999" className={inputCls + " mt-1"} />
          </div>
          <button onClick={testar} disabled={sending} data-testid="wa-teste-enviar"
                  className="btn-primary-blue h-10 px-5 text-[12px] font-medium flex items-center gap-1.5 shrink-0">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Enviando..." : "Enviar teste"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, accent }) {
  const bg = accent === "emerald" ? "bg-emerald-50" : accent === "rose" ? "bg-rose-50" : "bg-neutral-50";
  return (
    <div className={`rounded-lg p-2.5 ${bg}`}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-serif-display text-base mt-0.5 leading-tight">{value}</div>
    </div>
  );
}

function Historico() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [tipo, setTipo] = useState("");
  const [sucesso, setSucesso] = useState("");

  const load = async (targetPage = 1) => {
    const params = new URLSearchParams({ page: String(targetPage), page_size: "30" });
    if (tipo) params.set("tipo", tipo);
    if (sucesso === "ok") params.set("sucesso", "true");
    if (sucesso === "fail") params.set("sucesso", "false");
    try {
      const { data } = await masterApi.get(`/master/whatsapp/historico?${params.toString()}`);
      setItems(data.items || []);
      setPage(data.page || targetPage);
      setTotalPages(data.total_pages || 0);
      setTotal(data.total || 0);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(1); }, [tipo, sucesso]);

  return (
    <div className="space-y-3" data-testid="wa-historico-section">
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="hist-tipo"
                  className={inputCls + " mt-1 w-44"}>
            <option value="">Todos</option>
            {Object.entries(TIPOS_LABEL).map(([k, lbl]) => (<option key={k} value={k}>{lbl}</option>))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Status</label>
          <select value={sucesso} onChange={(e) => setSucesso(e.target.value)} data-testid="hist-sucesso"
                  className={inputCls + " mt-1 w-36"}>
            <option value="">Todos</option>
            <option value="ok">Sucesso</option>
            <option value="fail">Falha</option>
          </select>
        </div>
        <div className="ml-auto text-[11px] text-neutral-500">{total} envios</div>
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Quando</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Tent.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Detalhe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((m, idx) => (
              <TableRow key={idx} data-testid={`hist-row-${idx}`}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(m.criado_em).toLocaleString("pt-BR")}</TableCell>
                <TableCell className="text-xs">{TIPOS_LABEL[m.tipo] || m.tipo}</TableCell>
                <TableCell className="text-xs">{m.telefone}</TableCell>
                <TableCell>
                  {m.sucesso
                    ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(16,185,129,0.18)", color: "#047857" }}>OK</span>
                    : <span className="chip text-[10px] py-0.5" style={{ background: "rgba(244,63,94,0.18)", color: "#9F1239" }}>Falha</span>}
                </TableCell>
                <TableCell className="text-xs">{m.tentativas || 1}</TableCell>
                <TableCell className="text-xs text-neutral-500 max-w-[280px] truncate" title={m.detalhes}>{m.detalhes || "—"}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-6">Nenhum envio.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <button data-testid="hist-prev" disabled={page <= 1} onClick={() => load(page - 1)}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
            <ChevronLeft size={13} /> Anterior
          </button>
          <span className="text-[11px] text-neutral-600">Página <b>{page}</b> de <b>{totalPages}</b></span>
          <button data-testid="hist-next" disabled={page >= totalPages} onClick={() => load(page + 1)}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
            Próxima <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function EnvioUnico() {
  const [tipo, setTipo] = useState("sumidos");
  const [dias, setDias] = useState(30);
  const [items, setItems] = useState([]);

  const load = async () => {
    const params = new URLSearchParams({ tipo });
    if (tipo === "sumidos") params.set("dias", String(dias));
    try {
      const { data } = await masterApi.get(`/master/clientes-insights?${params.toString()}`);
      setItems(data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [tipo, dias]);

  const enviar = async (cliente_id) => {
    try {
      const { data } = await masterApi.post("/master/whatsapp/saudade", { cliente_id });
      if (data.ok) modal.success("Cupom enviado · cliente pode usar no próximo abastecimento");
      else modal.error("Falha no envio. Use o link manual ou marque como enviado.");
      load();
      return data.link_manual;
    } catch (e) { modal.error(apiError(e)); }
  };

  const copiarLink = async (cliente_id) => {
    const link = await enviar(cliente_id);
    if (link) {
      try { await navigator.clipboard.writeText(link); modal.success("Link copiado"); }
      catch { modal.info(link); }
    }
  };

  const marcarEnviado = async (cliente_id) => {
    try {
      await masterApi.post("/master/whatsapp/marcar-enviado", { cliente_id });
      modal.success("Marcado como enviado");
      load();
    } catch (e) { modal.error(apiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="card-soft rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Segmento</label>
          <select data-testid="wa-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputCls + " mt-1 w-56"}>
            <option value="sumidos">Clientes Sumidos</option>
            <option value="primeiro_nao_voltou">1º Cadastro sem retorno</option>
            <option value="saudade_enviado">Saudade já enviado</option>
          </select>
        </div>
        {tipo === "sumidos" && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Dias</label>
            <input data-testid="wa-dias" type="number" value={dias} onChange={(e) => setDias(parseInt(e.target.value) || 30)}
                   className={inputCls + " mt-1 w-24"} />
          </div>
        )}
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Nome</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Placa</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Último</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Abast.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Saudade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`wa-row-${c.id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell>{formatPhone(c.telefone)}</TableCell>
                <TableCell>{c.placa}</TableCell>
                <TableCell className="text-xs">{c.ultimo_abastecimento_em ? new Date(c.ultimo_abastecimento_em).toLocaleDateString("pt-BR") : "—"}</TableCell>
                <TableCell>{c.total_abastecimentos || 0}</TableCell>
                <TableCell>{c.cupom_saudade_ativo ? <Check size={15} className="inline text-emerald-600" /> : "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button data-testid={`wa-send-${c.id}`} onClick={() => enviar(c.id)}
                            className="btn-primary-blue h-8 px-3 text-[11px] font-medium flex items-center gap-1">
                      <Send size={11} /> Enviar
                    </button>
                    <button data-testid={`wa-link-${c.id}`} onClick={() => copiarLink(c.id)}
                            title="Copiar link" className="icon-action">
                      <Link2 size={14} />
                    </button>
                    <button data-testid={`wa-mark-${c.id}`} onClick={() => marcarEnviado(c.id)}
                            title="Marcar enviado" className="icon-action icon-action--green">
                      <CheckCircle2 size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-6">Nenhum cliente no segmento.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
